import React, { useState, useContext, useEffect, useRef } from 'react'
import { Button, FormControl, InputGroup, ListGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import SelectField from '../forms/SelectField'
import { apiGet } from 'api'
import AuthContext from 'context/auth-context'
import { useQuery } from 'react-query'
import EmployeeSearch from 'dashboard/filters/employees'

function ReferenceFilterPopover({
  column,
  params,
  setParams,
  onClose,
  onSetHandleApply,
  canEdit = true,
  hideApplyButton = false,
  initialSearch = ''
}) {
  const token = useContext(AuthContext)
  const accessorKey = column.accessor_key
  const [currentValue, setCurrentValue] = useState(params[accessorKey] || '')
  const [pendingParams, setPendingParams] = useState({
    employees: params.employees || [],
    excludedEmployees: params.excludedEmployees || []
  })
  const [selectSearch, setSelectSearch] = useState(initialSearch)

  // Track initial values to detect changes
  const initialValue = useRef(params[accessorKey] || '')
  const initialEmployees = useRef([...(params.employees || [])])
  const initialExcludedEmployees = useRef([...(params.excludedEmployees || [])])

  // Sync selectSearch when initialSearch changes
  useEffect(() => {
    if (initialSearch) {
      setSelectSearch(initialSearch)
    }
  }, [initialSearch])

  // Sync pendingParams with current params when they change (e.g., popover reopens)
  useEffect(() => {
    // Only sync employee-related params to avoid overwriting other changes
    const employeeParams = {
      employees: params.employees || [],
      excludedEmployees: params.excludedEmployees || []
    }
    setPendingParams(employeeParams)
  }, [params.employees, params.excludedEmployees])

  const { isSuccess, data: referenceOptions = [] } = useQuery(
    ['reference-options', params.dashboardId, accessorKey],
    () =>
      apiGet({
        url: 'transactions',
        token,
        params: {
          dashboard_id: params.dashboardId,
          reference_options_only: true,
          include_reference_options: true
        }
      }).then((res) => {
        return res.reference_options
      })
  )

  const handleApply = () => {
    // Check if any values have changed
    const valueChanged = currentValue !== initialValue.current

    // Compare employee arrays
    const employeesChanged =
      (pendingParams.employees || []).length !==
        initialEmployees.current.length ||
      !(pendingParams.employees || []).every(
        (emp, idx) =>
          JSON.stringify(emp) === JSON.stringify(initialEmployees.current[idx])
      )

    const excludedEmployeesChanged =
      (pendingParams.excludedEmployees || []).length !==
        initialExcludedEmployees.current.length ||
      !(pendingParams.excludedEmployees || []).every(
        (emp, idx) =>
          JSON.stringify(emp) ===
          JSON.stringify(initialExcludedEmployees.current[idx])
      )

    const hasChanges =
      valueChanged || employeesChanged || excludedEmployeesChanged

    if (hasChanges) {
      // Get the current params to ensure we have the latest sort state
      setParams((currentParams) => {
        const finalParams = {
          ...currentParams,
          ...pendingParams
        }
        if (!isEmployeeReference) {
          finalParams[accessorKey] = currentValue
        }
        return finalParams
      })
    }
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) {
      onSetHandleApply(handleApply)
    }
  }, [currentValue, pendingParams]) // eslint-disable-line react-hooks/exhaustive-deps

  const referenceData = referenceOptions[column.database_column_name]
  const options = referenceData?.options || referenceData || []
  const isEmployeeReference = referenceData?.class_name === 'Employee'

  // Auto-apply changes when hideApplyButton is true
  useEffect(() => {
    if (hideApplyButton) {
      setParams((currentParams) => {
        const finalParams = {
          ...currentParams,
          ...pendingParams
        }
        if (!isEmployeeReference) {
          finalParams[accessorKey] = currentValue
        }
        return finalParams
      })
    }
  }, [
    currentValue,
    pendingParams,
    hideApplyButton,
    setParams,
    accessorKey,
    isEmployeeReference
  ])

  const handleClear = () => {
    setCurrentValue('')
    setPendingParams({})
    const newParams = {
      ...params,
      [accessorKey]: ''
    }
    setParams(newParams)
  }

  const handleSelectChange = (newParams) => {
    if (!canEdit) return
    const newValue = newParams[accessorKey]
    setCurrentValue(newValue)
  }

  const handleEmployeeSearchChange = (newParams) => {
    setPendingParams(newParams)
  }

  return (
    <div>
      <div className="mb-3">
        <strong>{column.name}</strong>
      </div>

      {isEmployeeReference && (
        <EmployeeSearch
          params={{
            ...params,
            employees: pendingParams.employees || [],
            excludedEmployees: pendingParams.excludedEmployees || [],
            dashboardId: params.dashboardId || params.dashboard_id,
            section_id: params.section_id
          }}
          setParams={handleEmployeeSearchChange}
          columnName={column.database_column_name}
          canEdit={canEdit}
          restrictedOptions={options}
          initialSearch={initialSearch}
        />
      )}
      {!isEmployeeReference && isSuccess && (
        selectSearch || initialSearch ? (
          <div>
            <InputGroup className="mb-2">
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <FormControl
                placeholder={`Search ${column.name}`}
                value={selectSearch}
                onChange={(e) => canEdit && setSelectSearch(e.target.value)}
                disabled={!canEdit}
              />
            </InputGroup>
            <ListGroup variant="flush" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {options
                .filter((opt) =>
                  opt.label.toLowerCase().includes(selectSearch.toLowerCase())
                )
                .map((opt) => (
                  <ListGroup.Item
                    key={opt.value}
                    action={canEdit}
                    active={currentValue === opt.value}
                    onClick={() => {
                      if (!canEdit) return
                      setCurrentValue(currentValue === opt.value ? '' : opt.value)
                    }}
                    className="py-2"
                  >
                    {opt.label}
                  </ListGroup.Item>
                ))}
              {options.filter((opt) =>
                opt.label.toLowerCase().includes(selectSearch.toLowerCase())
              ).length === 0 && (
                <ListGroup.Item className="text-muted text-center py-2">
                  No matches found
                </ListGroup.Item>
              )}
            </ListGroup>
          </div>
        ) : (
          <SelectField
            params={{ [accessorKey]: currentValue }}
            setParams={handleSelectChange}
            options={options}
            accessorKey={accessorKey}
            title={column.name}
            placeholder={`Select ${column.name}`}
            hideClearButton={true}
            disabled={!canEdit}
          />
        )
      )}

      {!hideApplyButton && (
        <div className="d-flex justify-content-between gap-2 mt-3">
          {canEdit && !isEmployeeReference && (
            <Button variant="light" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          {canEdit && isEmployeeReference && (params.employees?.length > 0 || params.excludedEmployees?.length > 0) && (
            <Button variant="light" size="sm" onClick={() => {
              setPendingParams({ employees: [], excludedEmployees: [] })
            }}>
              Clear All
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={canEdit ? handleApply : onClose}
          >
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ReferenceFilterPopover
